import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function BitcoinTechPage75() {
  const contentRef = useRef(null);
  const titleText = "Digital SIgnatures";
  const links = {
    prev: "/bitcoin-tech-page-74",
    index: "/index-bitcoin-tech",
    next: "/bitcoin-tech-page-76",
  };
  const questions = [
    {
      text: "1. Why does Bitcoin use a stack-based scripting language for transaction validation?",
      options: [
        {
          value: "A",
          label: "A. To allow complex loops and conditional branching",
        },
        {
          value: "B",
          label:
            "B. To ensure deterministic execution and prevent unexpected behavior",
        },
        {
          value: "C",
          label: "C. To make scripts faster by allowing parallel execution",
        },
        {
          value: "D",
          label: "D. To enable scripts to modify the blockchain state directly",
        },
      ],
      correct: "B",
    },
    {
      text: "2. What is the primary purpose of OP_CHECKSIG in Bitcoin Script?",
      options: [
        {
          value: "A",
          label: "A. To generate a new public key from a private key",
        },
        {
          value: "B",
          label:
            "B. To verify that a digital signature matches a given public key and transaction hash",
        },
        {
          value: "C",
          label: "C. To encrypt transaction data before broadcasting",
        },
        {
          value: "D",
          label: "D. To check whether a transaction fee is sufficient",
        },
      ],
      correct: "B",
    },
    {
      text: '3. In ECDSA, what role does the ephemeral value "k" play during signature creation?',
      options: [
        {
          value: "A",
          label: "A. It acts as the public key used to verify signatures",
        },
        {
          value: "B",
          label: "B. It determines the Bitcoin address of the sender",
        },
        {
          value: "C",
          label:
            "C. It is a random value used to compute the point R and ensure signature security",
        },
        {
          value: "D",
          label: "D. It replaces the private key during verification",
        },
      ],
      correct: "C",
    },
    {
      text: '4. Why is it critical that the value "k" in ECDSA remains secret and unique for each signature?',
      options: [
        {
          value: "A",
          label: "A. Because revealing k allows miners to reverse transactions",
        },
        {
          value: "B",
          label:
            "B. Because reuse or leakage of k can lead to recovery of the private key",
        },
        {
          value: "C",
          label: "C. Because k determines the transaction fee",
        },
        {
          value: "D",
          label: "D. Because k is required to generate the public key",
        },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={75}
        timerMinutes={60}
        currentLevel={6}
        unlockedBadges={11}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 75</b>! In this lesson, we build directly on our
        understanding of Bitcoin Script and opcodes to explore one of the most
        critical components of Bitcoin’s security model: digital signatures. We
        will examine how cryptographic signatures authorize transactions, how
        they are verified by the network, and why this process can be trusted
        without relying on any central authority. By grounding this discussion
        in the mechanics of ECDSA and Bitcoin’s deterministic scripting
        environment, today’s session lays the foundation for understanding how
        ownership, authenticity, and integrity are enforced at the protocol
        level.
        <br />
        <br />
        <b>Laying the Groundwork for Bitcoin’s Digital Signatures</b>
        <br />
        <br />
        In our journey through Bitcoin’s technical landscape, we have explored
        the intricacies of Bitcoin Script and its role in transaction
        validation. As we now delve deeper into digital signatures, it is
        essential to build upon our prior understanding of Bitcoin’s scripting
        language and its execution model.
        <br />
        Bitcoin transactions are not merely records of value transfers; they are
        programmable instructions embedded within a specialized scripting
        language. This language is stack-based, meaning each instruction is
        executed sequentially in a linear fashion, with no loops or conditional
        branching. This deterministic execution model ensures that every script
        runs exactly as intended, preventing unexpected behaviors.
        <br />
        One key aspect we have examined is that the number of instructions in a
        script directly impacts its execution time and memory requirements.
        Since Bitcoin nodes and miners must process these scripts every time a
        transaction is broadcasted, efficiency is crucial. Each script must be
        executed by miners to verify whether a transaction is valid before it
        can be included in a block.
        <br />
        A crucial step in this validation process is checking digital
        signatures. These cryptographic signatures serve as proof that the
        transaction sender possesses the private key associated with the
        specified Bitcoin address, without revealing the key itself. This
        ensures both authenticity (the transaction is indeed authorized by the
        owner) and integrity (the transaction has not been altered).
        <br />
        <br />
        In previous discussions, we have explored Bitcoin’s opcodes that
        facilitate this verification. The most significant among them are:
        <ul>
          <li>
            OP_CHECKSIG: This opcode verifies whether a digital signature
            corresponds to the provided public key and transaction hash. If the
            signature is valid, it returns `true`; otherwise, it fails.
          </li>
          <li>
            OP_CHECKSIGVERIFY: This opcode combines `OP_CHECKSIG` with an
            implicit `OP_VERIFY`, meaning the transaction is immediately
            invalidated if the signature verification fails.
          </li>
        </ul>
        These opcodes are fundamental to Bitcoin’s security model, ensuring that
        only authorized users can spend funds. However, to fully appreciate how
        these instructions operate under the hood, we must understand how
        digital signatures function at a cryptographic level.
        <br />
        At its core, a digital signature is a mathematical construct that allows
        one party to prove possession of a secret (the private key) without
        disclosing it. This is the bedrock of Bitcoin’s trustless
        system—transactions can be validated by the network without relying on a
        centralized authority.
        <br />
        In this session, we will break down the mechanics of Bitcoin’s digital
        signature scheme. We will explore the cryptographic principles that make
        it secure, understand how signatures are generated and verified, and see
        why the process is resistant to forgery.
        <br />
        By the end of this discussion, you will have a deep understanding of how
        Bitcoin ensures transaction authenticity through cryptographic
        signatures—one of the foundational pillars of its security model. Let’s
        dive in.
      </p>
      <h2>ECDSA Signatures</h2>
      <p classname="content">
        Digital signatures in Bitcoin rely on elliptic curve cryptography (ECC)
        to provide security and authenticity. At the core of this system is the
        private key, denoted as "e," which satisfies the equation:
        <br />
        <br />
        eG = P
        <br />
        <br />
        Here, "P" represents the public key, and "G" is a known generator point
        on the elliptic curve. The fundamental goal of signing is to generate a
        verifiable proof without revealing the private key.
        <br />
        <br />
        <b>Target Selection and Computation</b>
        <br />
        <br />
        A randomly chosen 256-bit number, "k," serves as an ephemeral key. Using
        this, we compute:
        <br />
        <br />
        kG = R
        <br />
        <br />
        The point "R" represents the target, though only its x-coordinate,
        denoted as "r," is used. This value "r" is incorporated into the signing
        process.
        <br />
        <br />
        <b>Establishing the Signature Equation</b> <br />
        <br />
        The digital signature must satisfy an equation that holds under the
        discrete logarithm problem, ensuring security:
        <br />
        <br />
        uG + vP = kG
        <br />
        <br />
        where:
        <br />
        <br />
        - "u" and "v" are values determined by the signer,
        <br />
        - "G" and "P" are publicly known,
        <br />
        - "k" is chosen randomly.
        <br />
        <br />
        Rearranging, we derive:
        <br />
        <br />
        vP = (k - u)G
        <br />
        <br />
        Since "v" is nonzero, dividing by "v" yields:
        <br />
        <br />
        P = ((k - u)/v)G
        <br />
        <br />
        giving the same structure as "eG = P." This implies that finding "e"
        without knowledge of "k" is equivalent to solving the discrete logarithm
        problem, which is computationally infeasible.
        <br />
        <br />
        <b>Incorporating the Message</b>
        <br />
        <br />
        A crucial aspect of digital signatures is linking them to a specific
        message. In Bitcoin, this is achieved using a cryptographic hash
        function. The message is hashed to produce "z," ensuring a fixed-length
        representation. The values "u" and "v" are then set as:
        <br />
        <br />
        u = z/s, v = r/s
        <br />
        <br />
        With "r" incorporated into "v," and "z" linked to "u," the intent of the
        signer is embedded into the signature.
        <br />
        <br />
        <b>Computing the Signature</b>
        <br />
        <br />
        The signature component "s" is determined as follows:
        <br />
        <br />
        s = (z + re)/k
        <br />
        <br />
        The final signature consists of the pair (r, s).
        <br />
        <br />
        <b>Verification Process</b>
        <br />
        <br />
        To verify a signature, the verifier reconstructs "R" using:
        <br />
        <br />
        uG + vP = R<br />
        <br />
        Since:
        <br />
        <br />
        u = z/s, v = r/s <br />
        <br />
        we substitute: <br />
        <br />
        (z/s)G + (r/s)P = ((z + re)/s)G <br />
        <br />
        Using "s = (z + re)/k," we derive: <br />
        <br />
        uG + vP = ((z + re) / ((z + re)/k))G = kG = R <br />
        <br />
        If the x-coordinate of "R" matches "r," the signature is valid.{" "}
      </p>
      <p className="content">
        <b>Importance of Secure Randomness</b>
        <br />
        <br />
        The security of this scheme depends heavily on keeping "k"
        unpredictable. If "k" is revealed, the private key can be computed
        since, e = (k - u)/v. Thus, ensuring that "k" is truly random and unique
        for each signature is essential to prevent key compromise. By adhering
        to these principles, Bitcoin's digital signature scheme ensures
        transaction authenticity and security, reinforcing the integrity of the
        network.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default BitcoinTechPage75;
